import React from 'react';
import { ActivityItem, Icon, IconButton, Spinner, SpinnerSize, Stack, useTheme } from '@fluentui/react';
import { useGetStorefrontAssetActivitiesQuery } from '../../../store/endpoints/StorefrontAssetEndpoints';
import { IStorefrontAssetActivity, StorefrontAssetActivityType } from '../../../models/Storefront';

// The audit trail for one asset. Mounted only once the details panel's Activity tab has been
// opened, so nothing is fetched for the common case of opening the panel to rename something.

interface IAssetActivityListProps {
    assetId: string;
}

const ACTIVITY_ICONS: Record<StorefrontAssetActivityType, string> = {
    uploaded: 'Upload',
    renamed: 'Rename',
    retagged: 'Tag',
    replaced: 'Refresh',
    promoted: 'Up',
    deleted: 'Delete',
    registered: 'SearchAndApps',
};

const ACTIVITY_LABELS: Record<StorefrontAssetActivityType, string> = {
    uploaded: 'Uploaded',
    renamed: 'Renamed',
    retagged: 'Tags updated',
    replaced: 'Image replaced',
    promoted: 'Promoted to Global',
    deleted: 'Deleted',
    registered: 'Registered by scan',
};

const activityIcon = (activityType?: string): string =>
    ACTIVITY_ICONS[activityType as StorefrontAssetActivityType] ?? 'Info';

const activityLabel = (activityType?: string): string =>
    ACTIVITY_LABELS[activityType as StorefrontAssetActivityType] ?? (activityType || 'Activity');

const AssetActivityList: React.FC<IAssetActivityListProps> = ({ assetId }) => {
    const theme = useTheme();
    const { data, isFetching, isError, refetch } = useGetStorefrontAssetActivitiesQuery(assetId, { skip: !assetId });
    const items = data?.items ?? [];

    return (
        <Stack tokens={{ childrenGap: '0.5em' }} style={{ marginTop: 8 }}>
            <Stack horizontal verticalAlign='center'>
                <span style={{ fontWeight: 600, flexGrow: 1 }}>Activity</span>
                <IconButton
                    iconProps={{ iconName: 'Refresh' }}
                    title='Refresh'
                    ariaLabel='Refresh activity'
                    disabled={isFetching}
                    onClick={() => { void refetch(); }}
                />
            </Stack>
            {isFetching && <Spinner size={SpinnerSize.small} label='Loading activity' labelPosition='right' />}
            {!isFetching && isError && (
                <div style={{ fontSize: 12, color: theme.palette.redDark }}>Failed to load activity.</div>
            )}
            {!isFetching && !isError && items.length === 0 && (
                <div style={{
                    padding: '1em',
                    border: `1px dashed ${theme.palette.neutralTertiaryAlt}`,
                    borderRadius: 4,
                    fontSize: 12,
                    color: theme.palette.neutralSecondary,
                }}>
                    No recorded activity yet. Actions on this asset are recorded from now on.
                </div>
            )}
            {!isFetching && !isError && items.length > 0 && (
                <Stack tokens={{ childrenGap: '1em' }} style={{ marginTop: '0.5em' }}>
                    {items.map((activity, i) => (
                        <AssetActivityRow key={activity.id ?? `${activity.timestamp}-${i}`} activity={activity} />
                    ))}
                </Stack>
            )}
        </Stack>
    );
};

const AssetActivityRow: React.FC<{ activity: IStorefrontAssetActivity }> = ({ activity }) => {
    const stamp = activity.timestamp ? new Date(activity.timestamp).toLocaleString() : '';
    const meta = [activity.user, stamp].filter(Boolean).join(' · ');

    return (
        <ActivityItem
            activityDescription={activity.message || activityLabel(activity.activityType)}
            timeStamp={meta}
            activityIcon={<Icon iconName={activityIcon(activity.activityType)} />}
        />
    );
};

export default AssetActivityList;
